export type ProjectId = 'neoflex' | 'roselune-site' | 'roselune-persona' | 'wedbeas'

export type PortfolioImage = {
  src: string
  width: number
  height: number
  objectPosition?: string
}

export type PortfolioProject = {
  id: ProjectId
  title: string
  liveUrl: string | null
  images: readonly PortfolioImage[]
  technologies: readonly string[]
}

export type LocalizedText = { el: string; en: string }

export type PricingValue =
  | { kind: 'from' | 'fixed'; amount: number; unit?: 'hour' | 'track' }
  | { kind: 'custom' }
  | { kind: 'assessment' }

export type PricingItem = {
  id: string
  label: LocalizedText
  price: PricingValue
}

export type PricingCategory = {
  id: string
  anchor: string
  title: LocalizedText
  description: LocalizedText
  bullets: readonly LocalizedText[]
  items: readonly PricingItem[]
  includes?: readonly LocalizedText[]
}

export type FeaturedPricingReference = {
  id: 'websites' | 'aiVideo' | 'audio' | 'support'
  categoryId: string
  itemId: string
}

export const productionOrigin = 'https://fmdigitalservices.gr'

export const siteConfig = {
  brandName: 'FOTIS MYTIKAS DIGITAL SERVICES',
  personalName: 'Fotis Mytikas',
  descriptor: 'Web Development • Digital Products • AI Video & Audio',
  siteUrl: productionOrigin,
  analyticsEnabled: false,
  showPricing: true,
  assets: {
    logoPath: '/fm-logo.webp',
    logoAvailable: true,
    hero: '/fotis-mytikas-hero.webp',
    profile: '/fotis-mytikas-profile.webp',
    facebookCoverReferenceAvailable: false,
    roseluneReferenceAvailable: false,
  },
  contact: {
    email: 'info@fmdigitalservices.gr' as string,
    telephone: '+306984288182' as string,
    viberBusiness: 'https://connect.viber.com/en/business/p306984288182' as string,
    whatsapp: '' as string,
    instagram: '' as string,
    facebook: '' as string,
    linkedin: '' as string,
    upwork: 'https://www.upwork.com/freelancers/~01ef01078cc2dea759' as string,
  },
  location: {
    el: 'Νέα Πεντέλη, Αττική, Ελλάδα',
    en: 'Nea Penteli, Attica, Greece',
    remoteEl: 'Διαθέσιμος για απομακρυσμένη συνεργασία παγκοσμίως',
    remoteEn: 'Available remotely worldwide',
  },
  theme: {
    defaultMode: 'auto' as const,
    latitude: 38.05,
    longitude: 23.86,
    timezone: 'Europe/Athens',
    storageKey: 'fm-theme-mode',
  },
  pricing: {
    currency: 'EUR',
    symbol: '€',
    featured: [
      { id: 'websites', categoryId: 'websites', itemId: 'landing-page' },
      { id: 'aiVideo', categoryId: 'ai-video', itemId: 'ai-video-production' },
      { id: 'audio', categoryId: 'audio', itemId: 'audio-cleanup' },
      { id: 'support', categoryId: 'technical-support', itemId: 'support-hour' },
    ] satisfies readonly FeaturedPricingReference[],
    categories: [
      {
        id: 'websites',
        anchor: 'web-development',
        title: { el: 'Ιστοσελίδες, Web Εφαρμογές & Ψηφιακά Προϊόντα', en: 'Websites, Web Applications & Digital Products' },
        description: {
          el: 'Κατασκευάζω γρήγορες, responsive ιστοσελίδες και ψηφιακά προϊόντα για επιχειρήσεις, δημιουργούς και επαγγελματίες, με σύγχρονο σχεδιασμό, σωστή λειτουργία σε κάθε συσκευή και καθαρή τεχνική βάση.',
          en: 'I build fast, responsive websites and digital products for businesses, creators and professionals, with modern design, reliable behaviour across devices and a clean technical foundation.',
        },
        bullets: [
          { el: 'Landing pages για υπηρεσίες, προϊόντα και καμπάνιες', en: 'Landing pages for services, products and campaigns' },
          { el: 'Επαγγελματικές ιστοσελίδες και portfolios', en: 'Professional websites and portfolios' },
          { el: 'Web εφαρμογές και εμπειρίες τύπου εφαρμογής', en: 'Web applications and app-like experiences' },
          { el: 'Ανάπτυξη με React και σύγχρονο frontend', en: 'React and modern frontend development' },
          { el: 'Ανασχεδιασμός UI/UX και βελτίωση υπάρχουσας ιστοσελίδας', en: 'UI/UX redesign and existing-site improvements' },
          { el: 'Βελτιστοποίηση ταχύτητας, responsive layout και διορθώσεις frontend', en: 'Performance optimisation, responsive layout and frontend fixes' },
          { el: 'Δημοσίευση στο Netlify και σύνδεση domain', en: 'Netlify deployment and domain connection' },
          { el: 'Ιδέες εφαρμογών, πρωτότυπα και υποστήριξη Android', en: 'App concepts, prototypes and Android support' },
          { el: 'Εξατομικευμένο λογισμικό και ψηφιακές λειτουργίες', en: 'Custom software and digital functionality' },
        ],
        items: [
          { id: 'landing-page', label: { el: 'Landing page', en: 'Landing page' }, price: { kind: 'from', amount: 150 } },
          { id: 'business-website', label: { el: 'Επαγγελματική ιστοσελίδα', en: 'Professional website' }, price: { kind: 'from', amount: 350 } },
          { id: 'web-app-digital-product', label: { el: 'Web εφαρμογή ή ψηφιακό προϊόν', en: 'Web application or digital product' }, price: { kind: 'from', amount: 650 } },
          { id: 'performance-fixes', label: { el: 'Βελτιστοποίηση απόδοσης & διορθώσεις frontend', en: 'Performance optimisation & frontend fixes' }, price: { kind: 'from', amount: 50 } },
          { id: 'react-frontend', label: { el: 'Ανάπτυξη με React & frontend', en: 'React & frontend development' }, price: { kind: 'custom' } },
          { id: 'ui-ux-redesign', label: { el: 'Ανασχεδιασμός UI/UX & βελτίωση ιστοσελίδας', en: 'UI/UX redesign & website improvement' }, price: { kind: 'custom' } },
          { id: 'netlify-deployment', label: { el: 'Δημοσίευση στο Netlify & σύνδεση domain', en: 'Netlify deployment & domain connection' }, price: { kind: 'custom' } },
          { id: 'application-development', label: { el: 'Ανάπτυξη εφαρμογής', en: 'Application development' }, price: { kind: 'custom' } },
          { id: 'mobile-app-support', label: { el: 'Ιδέες εφαρμογών, πρωτότυπα & υποστήριξη Android', en: 'Mobile app concepts, prototypes & Android support' }, price: { kind: 'custom' } },
          { id: 'custom-software', label: { el: 'Εξατομικευμένο λογισμικό & ψηφιακές λειτουργίες', en: 'Custom software & digital functionality' }, price: { kind: 'custom' } },
        ],
      },
      {
        id: 'ai-video',
        anchor: 'ai-multimedia',
        title: { el: 'AI Video & Πολυμέσα', en: 'AI Video & Multimedia' },
        description: {
          el: 'Δημιουργώ video με υποστήριξη AI για social media, brands, καλλιτέχνες και μουσικά projects, με ανθρώπινη δημιουργική κατεύθυνση, προσεκτικό editing και συνεπή οπτική ταυτότητα.',
          en: 'I produce AI-assisted video for social media, brands, artists and music projects, combining human creative direction, careful editing and a consistent visual identity.',
        },
        bullets: [
          { el: 'Παραγωγή AI video για εταιρείες, καλλιτέχνες και μουσικά projects', en: 'AI video production for brands, artists and music projects' },
          { el: 'Επεξεργασία video, lip-sync και compositing', en: 'Video editing, lip-sync and compositing' },
          { el: 'Προωθητικά videos και περιεχόμενο καμπάνιας', en: 'Promotional videos and campaign content' },
          { el: 'Μουσικά videos και οπτική αφήγηση', en: 'Music videos and visual storytelling' },
          { el: 'Συνέπεια χαρακτήρα και σύνθεση σε πραγματικά περιβάλλοντα', en: 'Character consistency and integration into real environments' },
          { el: 'Τελικά αρχεία για social media και ψηφιακές πλατφόρμες', en: 'Final exports for social media and digital platforms' },
        ],
        items: [
          { id: 'ai-video-production', label: { el: 'Παραγωγή AI video', en: 'AI video production' }, price: { kind: 'from', amount: 100 } },
          { id: 'video-editing-compositing', label: { el: 'Επεξεργασία video, lip-sync & compositing', en: 'Video editing, lip-sync & compositing' }, price: { kind: 'from', amount: 180 } },
          { id: 'promotional-video', label: { el: 'Προωθητικό video περίπου 30–60″', en: 'Promotional video, approximately 30–60 seconds' }, price: { kind: 'from', amount: 180 } },
          { id: 'advanced-ai-video', label: { el: 'Προηγμένο AI video ή παραγωγή μουσικού video', en: 'Advanced AI video or music-video production' }, price: { kind: 'from', amount: 450 } },
          { id: 'complex-visual-production', label: { el: 'Σύνθετη παραγωγή πολλών σκηνών, lip-sync ή compositing', en: 'Complex multi-scene, lip-sync or compositing production' }, price: { kind: 'custom' } },
        ],
      },
      {
        id: 'ai-personas',
        anchor: 'ai-personas',
        title: { el: 'AI Personas & Εικονικοί Καλλιτέχνες', en: 'AI Personas & Virtual Artists' },
        description: {
          el: 'Αναπτύσσω ταυτότητα, οπτική κατεύθυνση και περιεχόμενο για AI personas, εικονικούς καλλιτέχνες και ψηφιακούς χαρακτήρες, με έμφαση στη συνέπεια και τη μακροχρόνια εξέλιξη.',
          en: 'I develop identity, visual direction and content for AI personas, virtual artists and digital characters, with an emphasis on consistency and long-term development.',
        },
        bullets: [
          { el: 'Ταυτότητα χαρακτήρα και οπτική κατεύθυνση', en: 'Character identity and visual direction' },
          { el: 'Συνεπείς ροές εικόνας και video', en: 'Consistent image and video workflows' },
          { el: 'Lip-sync, ήχος και περιεχόμενο χαρακτήρα', en: 'Lip-sync, audio and character content' },
          { el: 'Branding, ιστοσελίδα και παρουσία στα social media', en: 'Branding, website and social-media presence' },
          { el: 'Μουσικά videos και προωθητικές παραγωγές', en: 'Music videos and promotional productions' },
        ],
        items: [
          { id: 'persona-concept', label: { el: 'Ταυτότητα & αρχικό οπτικό concept AI persona', en: 'AI persona identity & initial visual concept' }, price: { kind: 'from', amount: 300 } },
          { id: 'persona-starter', label: { el: 'Ταυτότητα persona, οπτική κατεύθυνση και αρχικό πακέτο περιεχομένου', en: 'Persona identity, visual direction and starter content package' }, price: { kind: 'from', amount: 600 } },
          { id: 'complete-virtual-artist', label: { el: 'Ολοκληρωμένο σύστημα εικονικού καλλιτέχνη ή AI persona', en: 'Complete virtual artist or AI persona system' }, price: { kind: 'custom' } },
        ],
        includes: [
          { el: 'ταυτότητα χαρακτήρα', en: 'character identity' },
          { el: 'ροές εικόνας και video', en: 'image and video workflows' },
          { el: 'lip-sync', en: 'lip-sync' },
          { el: 'ήχο', en: 'audio' },
          { el: 'branding', en: 'branding' },
          { el: 'ιστοσελίδα', en: 'website' },
          { el: 'social περιεχόμενο', en: 'social content' },
          { el: 'music video ή προωθητική παραγωγή', en: 'music-video or promotional production' },
        ],
      },
      {
        id: 'audio',
        anchor: 'audio-production',
        title: { el: 'Παραγωγή Ήχου', en: 'Audio Production' },
        description: {
          el: 'Καθαρίζω, βελτιώνω και επεξεργάζομαι ήχο για μουσική, video, podcast, voiceover και ψηφιακό περιεχόμενο, με καθαρό και ισορροπημένο αποτέλεσμα έτοιμο για τη σωστή πλατφόρμα.',
          en: 'I clean, restore and improve audio for music, video, podcasts, voiceover and digital content, delivering a clear, balanced result prepared for the right platform.',
        },
        bullets: [
          { el: 'Καθαρισμός, βελτίωση και αποκατάσταση ήχου', en: 'Audio cleanup, enhancement and restoration' },
          { el: 'Mixing και mastering μουσικής', en: 'Music mixing and mastering' },
          { el: 'Βελτίωση podcast και voiceover', en: 'Podcast and voiceover enhancement' },
          { el: 'Εξισορρόπηση έντασης και καθαρότητα φωνής', en: 'Level balancing and vocal clarity' },
          { el: 'Ηχητικά εφέ, sound design και πρωτότυπη μουσική', en: 'Sound effects, sound design and original music' },
          { el: 'Τελικά αρχεία για streaming, video και social media', en: 'Final exports for streaming, video and social media' },
        ],
        items: [
          { id: 'audio-cleanup', label: { el: 'Επεξεργασία & αποκατάσταση ήχου', en: 'Audio editing & restoration' }, price: { kind: 'from', amount: 60 } },
          { id: 'mixing-mastering', label: { el: 'Mixing & mastering', en: 'Mixing & mastering' }, price: { kind: 'from', amount: 80, unit: 'track' } },
          { id: 'podcast-voiceover', label: { el: 'Βελτίωση podcast & voiceover', en: 'Podcast & voiceover enhancement' }, price: { kind: 'from', amount: 50 } },
          { id: 'sound-design', label: { el: 'Sound design & πρωτότυπη μουσική', en: 'Sound design & original music' }, price: { kind: 'custom' } },
        ],
      },
      {
        id: 'social-visual',
        anchor: 'social-content',
        title: { el: 'Social Media & Οπτικό Περιεχόμενο', en: 'Social Media & Visual Content' },
        description: {
          el: 'Δημιουργώ videos, covers, banners και οπτικό υλικό για καμπάνιες, κυκλοφορίες και μια συνεπή επαγγελματική παρουσία στα social media.',
          en: 'I create videos, covers, banners and campaign visuals for launches and a consistent professional presence across social media.',
        },
        bullets: [
          { el: 'Σύντομα videos για Reels, TikTok και Shorts', en: 'Short-form video for Reels, TikTok and Shorts' },
          { el: 'Covers, banners και προωθητικά γραφικά', en: 'Covers, banners and promotional graphics' },
          { el: 'Οπτικό υλικό για καμπάνιες και κυκλοφορίες', en: 'Campaign and launch visuals' },
          { el: 'Πακέτα περιεχομένου για επαγγελματική παρουσία', en: 'Content packages for a professional online presence' },
          { el: 'Κατεύθυνση περιεχομένου και οπτική συνέπεια', en: 'Content direction and visual consistency' },
        ],
        items: [
          { id: 'social-content', label: { el: 'Video & οπτικό περιεχόμενο για social media', en: 'Social media video & visual content' }, price: { kind: 'from', amount: 40 } },
          { id: 'content-package', label: { el: 'Πακέτο οπτικού περιεχομένου', en: 'Visual-content package' }, price: { kind: 'custom' } },
          { id: 'campaign-package', label: { el: 'Υλικό καμπάνιας ή κυκλοφορίας', en: 'Campaign or launch-content package' }, price: { kind: 'custom' } },
        ],
      },
      {
        id: 'technical-support',
        anchor: 'technical-support',
        title: { el: 'Τεχνική Υποστήριξη', en: 'Technical Support' },
        description: {
          el: 'Παρέχω τοπική ή απομακρυσμένη τεχνική υποστήριξη για υπολογιστές και κινητές συσκευές, ρυθμίσεις, λογισμικό, λειτουργικά συστήματα, βασική ασφάλεια και βελτιστοποίηση απόδοσης.',
          en: 'I provide local or remote technical support for computers and mobile devices, including setup, software, operating systems, basic security and performance optimisation.',
        },
        bullets: [
          { el: 'Διάγνωση προβλημάτων υπολογιστών και κινητών', en: 'Computer and mobile troubleshooting' },
          { el: 'Ρύθμιση συσκευών, λογισμικού και λειτουργικών συστημάτων', en: 'Device, software and operating-system setup' },
          { el: 'Βελτιστοποίηση απόδοσης και βασική ασφάλεια', en: 'Performance optimisation and basic security' },
          { el: 'Ρύθμιση αντιγράφων ασφαλείας', en: 'Backup setup' },
          { el: 'Καθοδήγηση για αναβάθμιση εξοπλισμού', en: 'Hardware-upgrade guidance' },
          { el: 'Τοπική ή απομακρυσμένη βοήθεια όπου είναι διαθέσιμη', en: 'Local or remote assistance where available' },
        ],
        items: [
          { id: 'support-hour', label: { el: 'Τεχνική υποστήριξη υπολογιστών & κινητών', en: 'Computer & mobile technical support' }, price: { kind: 'fixed', amount: 40, unit: 'hour' } },
          { id: 'hardware-work', label: { el: 'Αναβάθμιση εξοπλισμού ή μεγαλύτερη τεχνική εργασία', en: 'Hardware upgrade or larger technical work' }, price: { kind: 'assessment' } },
        ],
      },
    ] satisfies readonly PricingCategory[],
    disclaimer: {
      el: 'Οι τιμές είναι ενδεικτικές και αποτελούν αρχική βάση υπολογισμού. Η τελική προσφορά διαμορφώνεται ανάλογα με τις ανάγκες, τη διάρκεια, τα εργαλεία παραγωγής και την πολυπλοκότητα του project.',
      en: 'Prices are indicative starting points. The final quote depends on the project scope, duration, production tools and complexity.',
    },
  },
  links: {
    roselune: 'https://roseluneeverly.com',
    roseluneMusicVideo: '' as string,
    roselunePress: '' as string,
  },
  portfolio: [
    {
      id: 'neoflex',
      title: 'Neoflex Hellas — Official Company Website',
      liveUrl: 'https://neoflexhellas.gr',
      images: [
        { src: '/neoflexhellasgrprojectsite1.webp', width: 1440, height: 642 },
        { src: '/neoflexhellasgrprojectsite2.webp', width: 1906, height: 1042 },
        { src: '/neoflexhellasgrprojectsite7.webp', width: 1000, height: 750 },
      ],
      technologies: ['HTML5', 'CSS3', 'Responsive Design', 'SEO', 'Performance'],
    },
    {
      id: 'roselune-site',
      title: 'Roselune Everly — Official Artist Website',
      liveUrl: 'https://roseluneeverly.com',
      images: [
        { src: '/roselune everly site2.webp', width: 1910, height: 909 },
        { src: '/roselune everly site8.webp', width: 1906, height: 913 },
        { src: '/roselune everly site13.webp', width: 372, height: 821, objectPosition: 'center top' },
      ],
      technologies: ['React', 'Bilingual UX', 'Media Player', 'Netlify', 'SEO'],
    },
    {
      id: 'roselune-persona',
      title: 'Roselune Everly — Hybrid AI Artist & Virtual Persona',
      liveUrl: 'https://roseluneeverly.com',
      images: [
        { src: '/roseluneeverlyhubridaipersonaproject2.webp', width: 1376, height: 768 },
        { src: '/roseluneeverlyhubridaipersonaproject1.webp', width: 2048, height: 2048 },
        { src: '/roseluneeverlyhubridaipersonaproject3.webp', width: 1036, height: 814 },
      ],
      technologies: ['AI Video', 'Audio Engineering', 'Creative Direction', 'Brand Storytelling'],
    },
    {
      id: 'wedbeas',
      title: 'WedBeas — Dating & Relocation Platform Website + Native App',
      liveUrl: 'https://wedbeas.com',
      images: [
        { src: '/wedbeas2.webp', width: 1892, height: 908 },
        { src: '/wedbeas3.webp', width: 1898, height: 908 },
        { src: '/wedbeas5.webp', width: 1893, height: 912 },
      ],
      technologies: ['React', 'Supabase', 'Android', 'Product Design', 'Maps'],
    },
  ] satisfies readonly PortfolioProject[],
} as const

export function getPricingItem(categoryId: string, itemId: string) {
  const categories = siteConfig.pricing.categories as readonly PricingCategory[]
  return categories.find((category) => category.id === categoryId)?.items.find((item) => item.id === itemId)
}

export const featuredWorkLinks = [
  { label: 'WedBeas', url: 'https://wedbeas.com/' },
  { label: 'NeoFlex Hellas', url: 'https://neoflexhellas.gr/' },
  { label: 'Roselune Everly', url: 'https://roseluneeverly.com/' },
] as const

export const publicSocialLinks = Object.entries(siteConfig.contact)
  .filter(([key, value]) => !['email', 'telephone', 'viberBusiness', 'whatsapp'].includes(key) && Boolean(value))
  .map(([platform, url]) => ({ platform, url }))
